// ISDP CUSTOM: Live-results region of the search modal — the SearchResultState
// render-prop that cleans/dedupes/ranks the raw resultSet and renders the SHARED
// grouped list (SearchResultGroups — the exact same component the home hero uses,
// so Cmd+K and the hero are one coherent tool), plus the loading, warming-up, and
// zero-result (with AI "did you mean" chips + browse shortcuts) states.
//
// Extracted verbatim from SearchModal; DOM and behaviour are unchanged. Rendered
// by the modal only when `term` is non-empty, so it always assumes a live term.
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchResultState } from '@backstage/plugin-search-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { rankResults } from './rankResults';
import { SearchResultGroups, limitFindings } from './SearchResultGroups';
import { SearchLogger } from './SearchLogger';
import { type CurrentDoc } from './useCurrentDoc';
import { searchMessages } from '../../i18n/search';

export interface SearchModalResultsProps {
  term: string;
  warming: boolean;
  scoped: boolean;
  currentDoc: CurrentDoc | null;
  suggestions: string[];
  setHasResults: (settled: boolean) => void;
  setTerm: (term: string) => void;
  setScoped: (scoped: boolean) => void;
  goToResult: (location: string) => void;
  handleResultClick: () => void;
  onClose: () => void;
}

export function SearchModalResults({
  term,
  warming,
  scoped,
  currentDoc,
  suggestions,
  setHasResults,
  setTerm,
  setScoped,
  goToResult,
  handleResultClick,
  onClose,
}: SearchModalResultsProps) {
  const navigate = useNavigate();
  const { t } = useTranslationRef(searchMessages);
  const resultsRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={resultsRef} className="devhub-search-modal__results">
      {/* SearchResultState hands us the raw resultSet from context; we clean/dedupe it and
          render the SHARED grouped list — the exact same component the home hero uses, so the
          Cmd+K menu and the hero are one coherent tool. */}
      <SearchResultState>
        {({ value }) => {
          // value is undefined while the query is in-flight — treat that as
          // a loading state, NOT a settled empty result. Only rank/report
          // once the query has resolved (value defined).
          const loading = value === undefined;
          // Limit by FINDING (entity), not flat row, so one tool's many doc pages don't fill the
          // modal and hide other tools. The modal is taller than the hero, so it shows more.
          const ranked = loading
            ? []
            : limitFindings(rankResults(value.results, term), {
                maxFindings: 8,
                maxDocsPerFinding: 4,
              });
          // Notify the suggestion hook whether the settled query has results.
          if (!loading) {
            const settled = ranked.length > 0;
            // Avoid setState during render by scheduling a microtask.
            Promise.resolve().then(() => setHasResults(settled));
          }
          // Zero-result heading: "warming up" (cold index) takes precedence over the
          // normal empty message. Computed here to avoid a nested ternary in the JSX.
          let noResultsTitle: string = t('modal.noResults');
          if (warming) noResultsTitle = t('warming');
          else if (scoped && currentDoc)
            noResultsTitle = t('modal.noResultsInDoc', { doc: currentDoc.name });
          return (
            <>
              {term && !loading && <SearchLogger term={term} count={ranked.length} />}
              {loading && (
                <div className="devhub-search-modal__loading" aria-live="polite">
                  <span className="devhub-search-modal__spinner" aria-hidden="true" />
                  {t('searching')}
                </div>
              )}
              {!loading && ranked.length === 0 && (
              <div className="devhub-search-modal__noresults">
                <p className="devhub-search-modal__noresults-title">
                  {noResultsTitle}
                </p>
                {!warming && (
                  <p className="devhub-search-modal__noresults-hint">
                    {t('modal.noResultsHint')}
                  </p>
                )}
                {/* AI-powered alternative query suggestions — only shown once
                    each candidate has been validated to actually return results. */}
                {suggestions.length > 0 && (
                  <div className="devhub-search-modal__suggest">
                    <span className="devhub-search-modal__suggest-label">
                      {t('modal.didYouMean')}:
                    </span>
                    {suggestions.map(s => (
                      <button
                        key={s}
                        type="button"
                        className="devhub-search-modal__suggest-chip"
                        onClick={() => setTerm(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {/* The most common zero-result cause is being scoped to one doc — offer a
                    one-click escape to search everything before falling back to browsing. */}
                {scoped && currentDoc && (
                  <button
                    type="button"
                    className="devhub-search-modal__noresults-everywhere"
                    onClick={() => setScoped(false)}
                  >
                    <odx-icon name="core::search" size="sm" />
                    {t('modal.searchEverywhere')}
                  </button>
                )}
                <div className="devhub-search-modal__shortcuts">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/catalog');
                      onClose();
                    }}
                  >
                    <odx-icon name="core::menu" size="sm" />
                    {t('modal.catalog')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/docs');
                      onClose();
                    }}
                  >
                    <odx-icon name="core::document" size="sm" />
                    {t('modal.docs')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/api-docs');
                      onClose();
                    }}
                  >
                    <odx-icon name="core::link" size="sm" />
                    {t('modal.apis')}
                  </button>
                </div>
              </div>
              )}
              {!loading && ranked.length > 0 && (
                <SearchResultGroups
                  results={ranked}
                  onSelect={r => {
                    goToResult(r.document.location);
                    handleResultClick();
                  }}
                />
              )}
            </>
          );
        }}
      </SearchResultState>
    </div>
  );
}
